import type {
  LDEvaluationDetail,
  LDFlagSet,
  LDInspectionFlagDetailsChangedHandler,
  LDInspectionFlagUsedHandler,
} from 'launchdarkly-js-sdk-common'

const recordFlagToMeticulous = (flagKey: string, value: unknown) => {
  // Only record if Meticulous is loaded
  if (!window.Meticulous?.context) {
    return
  }

  // Meticulous only accepts string or boolean values
  if (typeof value === 'string' || typeof value === 'boolean') {
    try {
      window.Meticulous.context.recordFeatureFlag(flagKey, value)
    } catch (error) {
      // Fail silently - don't break app if Meticulous recording fails
      console.error('[Meticulous] Failed to record feature flag:', flagKey, error)
    }
  }
}

/**
 * LaunchDarkly inspector that records flags when they're evaluated.
 * This runs on every flag evaluation (useFlags, variation, etc.)
 */
const flagUsedInspector: LDInspectionFlagUsedHandler = {
  type: 'flag-used',
  name: 'meticulous-flag-used',
  method: (flagKey: string, flagDetail: LDEvaluationDetail) => {
    recordFlagToMeticulous(flagKey, flagDetail.value)
  },
}

/**
 * LaunchDarkly inspector that records all flags when they're loaded/updated in bulk.
 * This captures the initial flag state when LaunchDarkly initializes or when flags are updated.
 */
const flagDetailsChangedInspector: LDInspectionFlagDetailsChangedHandler = {
  type: 'flag-details-changed',
  name: 'meticulous-flags-loaded',
  method: (flagValueMap: LDFlagSet) => {
    // Record all flags when they're loaded
    Object.entries(flagValueMap).forEach(([flagKey, flagDetail]) => {
      recordFlagToMeticulous(flagKey, flagDetail.value)
    })
  },
}

/**
 * Creates LaunchDarkly inspectors that automatically record all flag activity to Meticulous.
 * Includes both initial flag load and individual flag evaluations.
 */
export const createMeticulousInspectors = () => [flagUsedInspector, flagDetailsChangedInspector]
